import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import type CrmPeopleContact from '@/types/serializers/CrmPeopleContact'
import type CrmPeopleNote from '@/types/serializers/CrmPeopleNote'
import { ContactCard } from './ContactCard'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus } from 'lucide-react'

interface ContactWithNotes extends CrmPeopleContact {
  notes?: CrmPeopleNote[]
}

interface ContactsTabProps {
  contacts: ContactWithNotes[]
  organizationDonorId: number
  currentUserId: number
  withDiscarded?: boolean
  onReloadDetail?: () => void
  onArchivedToggle?: (checked: boolean) => void
}

interface ContactCreateFormProps {
  organizationDonorId: number
  onCreated: () => void
  onCancelled: () => void
}

function ContactCreateForm({ organizationDonorId, onCreated, onCancelled }: ContactCreateFormProps) {
  const { data, setData, post, processing, errors, reset } = useForm({
    contact: { first_name: '', last_name: '', email: '', phone: '', job_title: '' }
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(`/people/organization_donors/${organizationDonorId}/contacts`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        reset()
        onCreated()
      }
    })
  }

  return (
    <form onSubmit={handleSubmit} className="border rounded-md p-4 space-y-3 bg-background">
      <h4 className="text-sm font-medium">Nouveau contact</h4>
      <div className="space-y-2">
        <div>
          <Label htmlFor="contact-first-name" className="text-xs">
            Prenom <span className="text-destructive">*</span>
          </Label>
          <Input
            id="contact-first-name"
            value={data.contact.first_name}
            onChange={(e) => setData('contact', { ...data.contact, first_name: e.target.value })}
            placeholder="Prenom"
            className="h-8 text-sm"
          />
          {errors['contact.first_name'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.first_name']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="contact-last-name" className="text-xs">
            Nom <span className="text-destructive">*</span>
          </Label>
          <Input
            id="contact-last-name"
            value={data.contact.last_name}
            onChange={(e) => setData('contact', { ...data.contact, last_name: e.target.value })}
            placeholder="Nom"
            className="h-8 text-sm"
          />
          {errors['contact.last_name'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.last_name']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="contact-email" className="text-xs">Email</Label>
          <Input
            id="contact-email"
            type="email"
            value={data.contact.email}
            onChange={(e) => setData('contact', { ...data.contact, email: e.target.value })}
            placeholder="email@exemple.com"
            className="h-8 text-sm"
          />
          {errors['contact.email'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.email']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="contact-phone" className="text-xs">Telephone</Label>
          <Input
            id="contact-phone"
            value={data.contact.phone}
            onChange={(e) => setData('contact', { ...data.contact, phone: e.target.value })}
            placeholder="+216 XX XXX XXX"
            className="h-8 text-sm"
          />
          {errors['contact.phone'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.phone']}</p>
          )}
        </div>
        <div>
          <Label htmlFor="contact-job-title" className="text-xs">Poste</Label>
          <Input
            id="contact-job-title"
            value={data.contact.job_title}
            onChange={(e) => setData('contact', { ...data.contact, job_title: e.target.value })}
            placeholder="Titre du poste"
            className="h-8 text-sm"
          />
          {errors['contact.job_title'] && (
            <p className="text-xs text-destructive mt-1">{errors['contact.job_title']}</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="submit" size="sm" disabled={processing}>
          Creer
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancelled}>
          Annuler
        </Button>
      </div>
    </form>
  )
}

export function ContactsTab({
  contacts,
  organizationDonorId,
  currentUserId,
  withDiscarded = false,
  onReloadDetail,
  onArchivedToggle,
}: ContactsTabProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [includeArchived, setIncludeArchived] = useState(withDiscarded)
  const [showCreateForm, setShowCreateForm] = useState(false)

  function handleArchivedToggle(checked: boolean) {
    setIncludeArchived(checked)
    if (onArchivedToggle) {
      onArchivedToggle(checked)
    }
  }

  function handleToggle(contactId: number) {
    setExpandedId((prev) => (prev === contactId ? null : contactId))
  }

  function handleContactCreated() {
    setShowCreateForm(false)
    onReloadDetail?.()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
        </p>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="contacts-archived" className="text-sm">
              Inclure les archives
            </Label>
            <Switch
              id="contacts-archived"
              checked={includeArchived}
              onCheckedChange={handleArchivedToggle}
            />
          </div>
          {!showCreateForm && (
            <Button variant="outline" size="sm" onClick={() => setShowCreateForm(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Ajouter un contact
            </Button>
          )}
        </div>
      </div>

      {showCreateForm && (
        <ContactCreateForm
          organizationDonorId={organizationDonorId}
          onCreated={handleContactCreated}
          onCancelled={() => setShowCreateForm(false)}
        />
      )}

      {contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          Aucun contact pour cette organisation.
        </p>
      ) : (
        <div className="space-y-2">
          {contacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              organizationDonorId={organizationDonorId}
              currentUserId={currentUserId}
              expanded={expandedId === contact.id}
              onToggle={() => handleToggle(contact.id)}
              onReloadDetail={onReloadDetail}
            />
          ))}
        </div>
      )}
    </div>
  )
}
